/**
 * Which label version the review composer should send with its save.
 *
 * A save carries `expected_updated_at`, and the api refuses it (409,
 * "Feedback changed. Refresh before saving again.") unless it matches the
 * row's stored `updated_at`. Tags save right away through their own
 * endpoint and bump that timestamp, so a reviewer who tags first and saves
 * second used to hit the 409 on every such review until a page refresh.
 *
 * The rule: when the queue row comes back with a newer timestamp but the
 * FEEDBACK on it (notes, verdict, status) is still what the composer was
 * seeded from, nothing the reviewer is about to write over has changed —
 * adopt the new version. When the feedback itself moved (a teammate or
 * the nightly scan rewrote the row), keep the old version so the 409
 * still protects their work.
 *
 * A row OLDER than the current version is never adopted. The composer's
 * own save returns a fresher version than the queue row it still renders
 * until the refetch lands; sliding back to that row would send a stale
 * version on the next save and hit the 409 until a reload. Both values
 * come from the api's one datetime serializer (UTC, `Z` suffix), so the
 * comparison is exact.
 */

export interface FeedbackSnapshot {
  notes: string
  approved: boolean
  review_status: string
}

export function feedbackSnapshot(row: FeedbackSnapshot): FeedbackSnapshot {
  return { notes: row.notes, approved: row.approved, review_status: row.review_status }
}

export function sameFeedback(a: FeedbackSnapshot, b: FeedbackSnapshot): boolean {
  return a.notes === b.notes && a.approved === b.approved && a.review_status === b.review_status
}

export function nextVersion(args: {
  current: string | null
  rowVersion: string | null
  seed: FeedbackSnapshot
  row: FeedbackSnapshot
}): string | null {
  const { current, rowVersion, seed, row } = args
  if (rowVersion === current) return current
  if (isOlder(rowVersion, current)) return current
  return sameFeedback(seed, row) ? rowVersion : current
}

function isOlder(candidate: string | null, current: string | null): boolean {
  if (candidate === null || current === null) return false
  return Date.parse(candidate) < Date.parse(current)
}
